import { createContext, useContext, useRef, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import {
  DateCalendar,
  PickersDay,
  PickersDayProps,
  TimeField,
} from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import {
  DateRangeOptions,
  DateRangePosition,
  DateRangeValue,
  getDateRangeDateForDay,
  getDateRangeErrors,
  isDateRangeUnitDisabled,
  isValidRangeDate,
} from './dateRangeValidation';

const RangeContext = createContext({
  from: null as Date | null,
  to: null as Date | null,
  preview: false,
  onHover: (() => {}) as (day: Dayjs) => void,
});

const RangeDay = (props: PickersDayProps<Dayjs>) => {
  const range = useContext(RangeContext);
  const start = isValidRangeDate(range.from)
    ? dayjs(range.from).startOf('day')
    : null;
  const end = isValidRangeDate(range.to)
    ? dayjs(range.to).startOf('day')
    : null;
  const day = props.day.startOf('day');
  const first = !!start && day.isSame(start);
  const last = !!end && day.isSame(end);
  const between = !!start && !!end && day.isAfter(start) && day.isBefore(end);
  return (
    <PickersDay
      {...props}
      disableMargin
      selected={first || last}
      onMouseEnter={(event, date) => {
        props.onMouseEnter?.(event, date);
        if (!props.disabled) range.onHover(date);
      }}
      sx={{
        width: 40,
        borderRadius:
          first && !last && end
            ? '50% 0 0 50%'
            : last && !first && start
              ? '0 50% 50% 0'
              : between
                ? 0
                : '50%',
        ...(between && { bgcolor: range.preview ? '#fff3e8' : '#ffe3cc' }),
        '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus': {
          bgcolor: 'var(--primary-color, #ff7500)',
          color: '#fff',
        },
        '&.Mui-focusVisible': {
          outline: '2px solid #9a4600',
          outlineOffset: -2,
        },
      }}
    />
  );
};

interface DateRangePickerPanelProps extends DateRangeOptions {
  value: DateRangeValue;
  label: string;
  titleId: string;
  fromLabel: string;
  toLabel: string;
  onApply: (value: DateRangeValue) => void;
  onCancel: () => void;
}

const DateRangePickerPanel = ({
  value,
  label,
  titleId,
  fromLabel,
  toLabel,
  onApply,
  onCancel,
  withTime = false,
  allowOpenRange = false,
  required = false,
  blockedRanges = [],
}: DateRangePickerPanelProps) => {
  const [draft, setDraft] = useState(value);
  const selectedDays = useRef({ ...value });
  // Calendar navigation can emit a partial year/month value. Keep that cursor
  // separate from the range so browsing never commits a date or resets the view.
  const [calendarValue, setCalendarValue] = useState<Dayjs | null>(() => {
    const initial = value.from || value.to;
    return isValidRangeDate(initial) ? dayjs(initial) : null;
  });
  const [position, setPosition] = useState<DateRangePosition>('from');
  const [hover, setHover] = useState<Date | null>(null);
  const hasExistingRange = !!(value.from || value.to);
  const [chosen, setChosen] = useState({
    from: !!value.from || (allowOpenRange && hasExistingRange),
    to: !!value.to || (allowOpenRange && hasExistingRange),
  });
  const complete = chosen.from && chosen.to;
  const errors = complete
    ? getDateRangeErrors(draft, {
        withTime,
        allowOpenRange,
        required,
        blockedRanges,
      })
    : {};
  const errorMessage = errors.from || errors.to;
  const selectDay = (date: Dayjs | null) => {
    if (!date) return;
    const selected = getDateRangeDateForDay(
      date.toDate(),
      position,
      draft,
      blockedRanges,
      withTime
    );
    if (!selected) return;
    setCalendarValue(dayjs(selected));
    selectedDays.current[position] = selected;
    if (position === 'from') {
      selectedDays.current.to = null;
      setDraft({ from: selected, to: null });
      setChosen({ from: true, to: false });
      setPosition('to');
    } else {
      setDraft((previous) => ({ ...previous, to: selected }));
      setChosen((previous) => ({ ...previous, to: true }));
    }
    setHover(null);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ p: 2, pb: 0 }}>
        <Typography id={titleId} fontWeight={600}>
          {label}
        </Typography>
        <Typography
          variant='body2'
          color='text.secondary'
          aria-live='polite'
          sx={{ mt: 0.5, mb: 1.5 }}
        >
          {complete
            ? 'Revisá el período y aplicá la selección.'
            : position === 'from'
              ? 'Elegí la fecha de inicio.'
              : 'Ahora elegí la fecha de fin.'}
        </Typography>
        <Stack direction='row' spacing={1}>
          {(['from', 'to'] as const).map((side) => (
            <Box key={side} sx={{ flex: 1, minWidth: 0 }}>
              <Button
                fullWidth
                variant='outlined'
                aria-pressed={position === side}
                onClick={() => {
                  setPosition(side);
                  setHover(null);
                  const date = selectedDays.current[side];
                  if (isValidRangeDate(date)) setCalendarValue(dayjs(date));
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  flexDirection: 'column',
                  textTransform: 'none',
                  px: 1.5,
                  py: 1,
                  color: 'text.primary',
                  borderColor:
                    position === side
                      ? 'var(--primary-color, #ff7500)'
                      : 'divider',
                  bgcolor: position === side ? '#fff8f1' : 'transparent',
                }}
              >
                <Typography
                  component='span'
                  variant='caption'
                  color='text.secondary'
                >
                  {side === 'from' ? fromLabel : toLabel}
                </Typography>
                <Typography component='span' variant='body2' fontWeight={600}>
                  {isValidRangeDate(selectedDays.current[side])
                    ? dayjs(selectedDays.current[side]).format('DD MMM YYYY')
                    : chosen[side]
                      ? 'Sin límite'
                      : 'Seleccionar'}
                </Typography>
              </Button>
              {allowOpenRange && !required && (
                <Button
                  size='small'
                  onClick={() => {
                    selectedDays.current[side] = null;
                    setDraft((previous) => ({ ...previous, [side]: null }));
                    setChosen((previous) => ({ ...previous, [side]: true }));
                    setPosition(side === 'from' ? 'to' : 'from');
                    setHover(null);
                  }}
                  sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                  {side === 'from' ? 'Sin inicio' : 'Sin fin'}
                </Button>
              )}
            </Box>
          ))}
        </Stack>
      </Box>
      <RangeContext.Provider
        value={{
          from: draft.from,
          to: position === 'to' && !chosen.to && hover ? hover : draft.to,
          preview: !chosen.to,
          onHover: (date) => setHover(date.toDate()),
        }}
      >
        <Box onMouseLeave={() => setHover(null)}>
          <DateCalendar
            autoFocus
            value={calendarValue}
            referenceDate={
              isValidRangeDate(draft.from) ? dayjs(draft.from) : undefined
            }
            onChange={(date, state) => {
              setCalendarValue(date);
              if (state === 'finish') selectDay(date);
            }}
            slots={{ day: RangeDay }}
            showDaysOutsideCurrentMonth
            shouldDisableDate={(date) =>
              isDateRangeUnitDisabled(
                date.toDate(),
                position,
                'day',
                draft.from,
                blockedRanges,
                withTime
              )
            }
            sx={{
              width: '100%',
              height: 336,
              '& .MuiDayCalendar-weekDayLabel': { width: 40, m: 0 },
            }}
          />
        </Box>
      </RangeContext.Provider>
      <Box sx={{ px: 2, pb: 2 }}>
        {withTime && (
          <Stack direction='row' spacing={1} sx={{ mb: 1.5 }}>
            {(['from', 'to'] as const).map((side) => (
              <TimeField
                key={side}
                label={side === 'from' ? 'Hora de inicio' : 'Hora de fin'}
                value={draft[side] ? dayjs(draft[side]) : null}
                disabled={!draft[side]}
                onChange={(time) => {
                  const date = new Date(selectedDays.current[side] ?? NaN);
                  if (time?.isValid()) {
                    date.setHours(time.hour(), time.minute(), time.second());
                  } else {
                    date.setTime(NaN);
                  }
                  setDraft((previous) => ({
                    ...previous,
                    [side]: date,
                  }));
                }}
                referenceDate={
                  isValidRangeDate(selectedDays.current[side])
                    ? dayjs(selectedDays.current[side])
                    : undefined
                }
                ampm={false}
                format='HH:mm:ss'
                size='small'
                fullWidth
                slotProps={{ textField: { error: !!errors[side] } }}
                shouldDisableTime={(date, unit) =>
                  isDateRangeUnitDisabled(
                    date.toDate(),
                    side,
                    unit,
                    draft.from,
                    blockedRanges,
                    true
                  )
                }
              />
            ))}
          </Stack>
        )}
        {errorMessage && (
          <Typography
            role='alert'
            variant='caption'
            color='error'
            sx={{ display: 'block', mb: 1 }}
          >
            {errorMessage}
          </Typography>
        )}
        {!!blockedRanges.length && (
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ display: 'block', mb: 1.5 }}
          >
            Las fechas ocupadas están bloqueadas.
            {withTime && ' Los días con horas libres siguen disponibles.'}
          </Typography>
        )}
        <Stack direction='row' justifyContent='flex-end' spacing={1}>
          <Button
            onClick={onCancel}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Cancelar
          </Button>
          <Button
            variant='contained'
            disableElevation
            disabled={!complete || !!errorMessage}
            onClick={() => onApply(draft)}
            sx={{
              textTransform: 'none',
              bgcolor: 'var(--primary-color, #ff7500)',
              '&:hover': { bgcolor: '#d96100' },
            }}
          >
            Aplicar período
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default DateRangePickerPanel;
